// WHAT IS BEING SIGNED OFF, PICKED (owner 2026-11-05: "we will allow selection of any
// layer / mechanical, electrical, security, power, plumbing, underground, roof, process
// piping ... per area, per level, and we will link them to the activities; a user can
// select 1 or multiple systems, per 1 or multiple areas in 1 or multiple levels").
//
// Three lists - levels, areas, systems - and the cross product, collapsed the way the
// sign-off reads it (isoModel.scopeTargets):
//
//   a level with no area picked   the whole level
//   areas, no system              each area
//   areas and systems             each system IN each area
//
// Systems come in the owner's two groups, each labelled by where it comes from: the DRAWN
// LAYERS of the drawings (what the isometric draws) and the systems IN THE MODEL (what the
// element count is taken from).
//
// The activities each target would star are read from the server's own preview, so what is
// shown here is what the sign-off will record - nothing is guessed in the browser.
import React, { useMemo } from "react";
import { Alert, Button, Checkbox, Empty, Select, Tag, Tooltip } from "antd";
import { DRAWN_LAYERS, isLayer, layerIdOf, scopeTargets, systemOptions } from "./isoModel";

export default function ScopePicker({
  levels, levelNames, areas, tiles, manifest, scope, setScope, preview, loading, err, canRequest, onRequest,
}) {
  const levelOptions = useMemo(() => levels.map((l) => ({
    value: l.key, label: `${l.name} · ${l.elements.toLocaleString()} elements`,
  })), [levels]);

  // the areas offered: those on the levels picked, else every current area
  const areaOptions = useMemo(() => Object.entries(areas || {})
    .filter(([, a]) => a.current)
    .filter(([, a]) => !scope.levelKeys.length || scope.levelKeys.includes(a.levelKey))
    .map(([id, a]) => ({ value: id, label: [a.name, a.level].filter(Boolean).join(" · ") }))
    .sort((x, y) => x.label.localeCompare(y.label, undefined, { numeric: true })), [areas, scope.levelKeys]);

  // `tiles` is the tile VIEW ({ grid, tiles }); the counts are taken from its rows
  const rows = useMemo(() => (tiles && tiles.tiles) || [], [tiles]);
  const { layers, model } = useMemo(
    () => systemOptions({ areaIds: scope.areaIds, areas, tiles: rows, manifest }), [scope.areaIds, areas, rows, manifest],
  );
  const systemOpts = useMemo(() => [
    { label: "Drawn layers", options: layers.map((o) => ({ value: o.value, label: `${o.label} · ${o.note}`, disabled: false })) },
    { label: "In the model", options: model.map((o) => ({ value: o.value, label: `${o.label} · ${o.note}` })) },
  ], [layers, model]);

  // the TARGETS carry the timeline's own level names, which work back out to their keys on
  // the server; the drawing's labels may come from elsewhere (see isoModel.scopeTargets)
  const targets = useMemo(() => scopeTargets(scope, { areas, levels: levelNames || {} }), [scope, areas, levelNames]);
  const set = (patch) => setScope({ ...scope, ...patch });
  const labelOf = (t) => {
    if (t.kind === "level") return t.level;
    const a = areas[t.areaSetId] || {};
    return [t.system, a.name, a.level].filter(Boolean).join(" · ");
  };
  // a drawn layer the drawings do not carry yet: it is still signable, and says so
  const notDrawn = (scope.systems || []).filter(isLayer)
    .filter((s) => !layers.find((l) => l.value === s && l.drawn))
    .map((s) => (DRAWN_LAYERS.find((l) => l.id === layerIdOf(s)) || {}).label || layerIdOf(s));

  return (
    <div className="signoff-scope" data-testid="signoff-scope">
      <div className="signoff-panel-title">The scope to sign off</div>
      <label className="signoff-scope-label" htmlFor="scope-levels">Levels</label>
      <Select id="scope-levels" mode="multiple" allowClear style={{ width: "100%" }} placeholder="Every level"
              value={scope.levelKeys} onChange={(v) => set({ levelKeys: v })} options={levelOptions}
              optionFilterProp="label" maxTagCount="responsive" data-testid="scope-levels" />
      <label className="signoff-scope-label" htmlFor="scope-areas">Areas</label>
      <Select id="scope-areas" mode="multiple" allowClear style={{ width: "100%" }}
              placeholder={scope.levelKeys.length ? "The whole level" : "Pick the areas"}
              value={scope.areaIds} onChange={(v) => set({ areaIds: v })} options={areaOptions}
              showSearch optionFilterProp="label" maxTagCount="responsive" data-testid="scope-areas" />
      <label className="signoff-scope-label" htmlFor="scope-systems">Systems</label>
      <Tooltip title="A system is always signed off inside an area. Pick the areas first; with none picked the whole area or level is signed off.">
        <Select id="scope-systems" mode="multiple" allowClear style={{ width: "100%" }}
                placeholder={scope.areaIds.length ? "Everything in those areas" : "Pick an area first"}
                disabled={!scope.areaIds.length} value={scope.systems} onChange={(v) => set({ systems: v })}
                options={systemOpts} showSearch optionFilterProp="label" maxTagCount="responsive" data-testid="scope-systems" />
      </Tooltip>
      <Checkbox className="mt-2" checked={!!scope.only} onChange={(e) => set({ only: e.target.checked })} data-testid="scope-only">
        <Tooltip title="Draw only what is in the scope; the rest of the building stays pale.">Show the scope alone</Tooltip>
      </Checkbox>

      <div className="signoff-scope-targets" data-testid="scope-targets">
        {targets.length ? targets.map((t, i) => (
          <Tag key={i} color={t.kind === "system" ? "geekblue" : t.kind === "area" ? "blue" : "cyan"}>{labelOf(t)}</Tag>
        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pick a level or an area" />}
      </div>
      {notDrawn.length ? (
        <Alert type="info" showIcon className="mb-2" data-testid="scope-not-drawn"
               message={`${notDrawn.join(", ")} ${notDrawn.length === 1 ? "is" : "are"} not in this project's drawings yet`}
               description="The sign-off is still recorded against it; nothing is drawn for it and it covers no model elements." />
      ) : null}
      {err ? <Alert type="error" showIcon className="mb-2" message={err} /> : null}
      {targets.length ? (
        <div className="signoff-scope-facts">
          {!canRequest ? <small className="signoff-muted">The drawing shows what is signed off; asking for a sign-off is for the project owner, an admin or a schedule editor.</small>
            : loading ? <small className="signoff-muted">Working out what this covers…</small> : preview ? (
            <>
              <div className="tile-row"><span>Tiles</span><b>{preview.tiles.toLocaleString()}</b></div>
              <div className="tile-row"><span>Elements</span><b>{preview.elements.toLocaleString()}</b></div>
              <div className="tile-row"><span>Activities starred</span><b>{preview.activityCount.toLocaleString()}</b></div>
              {preview.activities.length ? (
                <small className="signoff-muted d-block" data-testid="scope-activities">
                  {preview.activities.slice(0, 8).map((a) => a.name).join(" · ")}
                  {preview.activities.length > 8 ? ` +${preview.activities.length - 8} more` : ""}
                </small>
              ) : <small className="signoff-muted d-block">No schedule activity is tied to this scope yet - the sign-off is still recorded.</small>}
              {preview.coverNote ? <Alert type="warning" showIcon className="mt-2" message={preview.coverNote} /> : null}
            </>
          ) : null}
        </div>
      ) : null}
      <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
        <Tooltip title="Clear the levels, areas and systems picked"><Button onClick={() => setScope({ levelKeys: [], areaIds: [], systems: [], only: false })} data-testid="scope-clear">Clear</Button></Tooltip>
        {canRequest ? (
          <Tooltip title="Ask the owner/GC, the contractor or a trade partner to sign this scope off by email">
            <Button type="primary" disabled={!targets.length} onClick={() => onRequest(targets)} data-testid="scope-request">Request sign-off</Button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}
